/**
 * Classifica erros da API que significam "esta sessão acabou".
 *
 * Contrato com o backend (QA de develop, 08/10/2026):
 * - token revogado (troca de senha ou "Sair"): `401 { code: "SESSION_REVOKED" }`;
 * - usuário inativo ou token que não é de usuário admin: `403 USER_INACTIVE`.
 *   Enquanto o backend não muda, a resposta é `403 { code: "FORBIDDEN",
 *   message: "Usuario inativo" }`, por isso a mensagem também é reconhecida.
 *
 * Um 403 de permissão comum nunca encerra a sessão.
 */

export const SESSION_REVOKED_CODE = "SESSION_REVOKED";
export const USER_INACTIVE_CODE = "USER_INACTIVE";

type ApiErrorLike = {
  status?: unknown;
  code?: unknown;
  message?: unknown;
  data?: unknown;
};

function readString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function normalizeText(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

function readError(error: unknown) {
  if (!error || typeof error !== "object") return null;
  const source = error as ApiErrorLike;
  const data =
    source.data && typeof source.data === "object"
      ? (source.data as { code?: unknown; message?: unknown })
      : null;
  return {
    status: Number(source.status),
    code: readString(source.code) || readString(data?.code),
    message: readString(data?.message) || readString(source.message),
  };
}

function isInactiveUser(parsed: NonNullable<ReturnType<typeof readError>>): boolean {
  if (parsed.status !== 403) return false;
  if (parsed.code === USER_INACTIVE_CODE) return true;
  return normalizeText(parsed.message) === "usuario inativo";
}

/**
 * Sessão encerrada de forma explícita pelo servidor. Serve para derrubar a
 * sessão a partir de qualquer chamada, sem confundir com um 401 de regra de
 * negócio (por exemplo, senha atual errada na troca de senha).
 */
export function isEndedAdminSessionError(error: unknown): boolean {
  const parsed = readError(error);
  if (!parsed) return false;
  if (parsed.status === 401 && parsed.code === SESSION_REVOKED_CODE) return true;
  return isInactiveUser(parsed);
}

/**
 * Resposta das chamadas que validam a sessão (`/auth/me`,
 * `/establishments/me`): qualquer 401, ou o 403 de usuário inativo.
 */
export function isInvalidAdminSessionError(error: unknown): boolean {
  const parsed = readError(error);
  if (!parsed) return false;
  if (parsed.status === 401) return true;
  return isInactiveUser(parsed);
}
